// pms-adapters — the application service over the season calendar and the rate
// profile (step 6 of PMS-SEASON-CALENDAR-BRIEF).
//
// This is where the two objects finally MEET. They are stored apart, versioned
// apart and edited apart — the whole point of the split — and composed here, in
// one direction, to produce a grid. `generateGrid` is pure and does the
// arithmetic; this service fetches what it needs, hands the result to the
// guarded write path, and reports what the database declined.
//
// It holds no rule of its own beyond the L2 capability gate. Anything that
// looks like a pricing decision in here would be a second implementation of the
// domain, and the one nobody tests.

import { CAPABILITIES, type ActorSource } from '@lemontaigu/platform';
import { err, generateGrid, ok } from '@lemontaigu/pms-core';
import type {
  BaseAdjustment,
  GenerateRequest,
  GenerationReport,
  IsoDate,
  PeriodPatch,
  ProposedPeriod,
  RateProfile,
  RateProfileRepository,
  RateRepository,
  Result,
  RoomRepository,
  SeasonCalendarRepository,
  SeasonPlanningService,
  StoredRateProfile,
  StoredSeasonCalendar,
} from '@lemontaigu/pms-core';
import { gate } from './services.js';

export class SeasonPlanningServiceImpl implements SeasonPlanningService {
  constructor(
    private readonly calendars_: SeasonCalendarRepository,
    private readonly profiles_: RateProfileRepository,
    private readonly rates: RateRepository,
    private readonly rooms: RoomRepository,
    private readonly actor: ActorSource,
  ) {}

  // ── Reads ────────────────────────────────────────────────────────────────

  calendars() {
    return this.read(() => this.calendars_.listCalendars());
  }
  calendar(id: number): Promise<Result<StoredSeasonCalendar | null>> {
    return this.read(() => this.calendars_.getCalendar(id));
  }
  baseHistory(periodId: number): Promise<Result<BaseAdjustment[]>> {
    return this.read(() => this.calendars_.baseHistory(periodId));
  }
  profiles(): Promise<Result<StoredRateProfile[]>> {
    return this.read(() => this.profiles_.listProfiles());
  }

  // ── Calendar writes ──────────────────────────────────────────────────────

  createCalendar(year: number, note: string | null) {
    return this.write(() => this.calendars_.createCalendar(year, note));
  }
  deleteCalendar(id: number) {
    return this.write(() => this.calendars_.deleteCalendar(id));
  }
  addPeriods(calendarId: number, periods: ProposedPeriod[]) {
    return this.write(() => this.calendars_.addPeriods(calendarId, periods));
  }
  updatePeriod(id: number, patch: PeriodPatch) {
    return this.write(() => this.calendars_.updatePeriod(id, patch));
  }
  deletePeriod(id: number) {
    return this.write(() => this.calendars_.deletePeriod(id));
  }
  addClosure(calendarId: number, from: IsoDate, to: IsoDate, reason: string) {
    return this.write(() => this.calendars_.addClosure(calendarId, from, to, reason));
  }
  deleteClosure(id: number) {
    return this.write(() => this.calendars_.deleteClosure(id));
  }

  // ── Profile writes ───────────────────────────────────────────────────────

  createProfileDraft(draft: RateProfile, note: string | null) {
    return this.write(() => this.profiles_.createProfileDraft(draft, note));
  }
  updateProfileDraft(id: number, draft: RateProfile, note: string | null) {
    // NOT validated, deliberately — the same asymmetry the parameter set had: a
    // half-authored profile must be savable, or the operator has to finish in
    // one sitting. Publishing is where structure is enforced, because that is
    // the irreversible step.
    return this.write(() => this.profiles_.updateProfileDraft(id, draft, note));
  }
  publishProfile(id: number) {
    return this.write(() => this.profiles_.publishProfile(id));
  }
  archiveProfile(id: number) {
    return this.write(() => this.profiles_.archiveProfile(id));
  }
  deleteProfileDraft(id: number) {
    return this.write(() => this.profiles_.deleteProfileDraft(id));
  }

  // ── The composition ──────────────────────────────────────────────────────

  async generate(input: GenerateRequest): Promise<Result<GenerationReport>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return denied;

    if (input.to < input.from) {
      return err('validation', 'La fin de la période précède son début.');
    }

    const [cal, prof, plans, types] = await Promise.all([
      this.calendars_.getCalendar(input.calendarId),
      this.profiles_.getProfile(input.profileId),
      this.rates.listPlans(),
      this.rooms.listRoomTypes(),
    ]);
    if (!cal.ok) return cal;
    if (!prof.ok) return prof;
    if (!plans.ok) return plans;
    if (!types.ok) return types;
    if (cal.value === null) return err('not_found', "Ce calendrier de saisons n'existe pas.");
    if (prof.value === null) return err('not_found', "Ce profil tarifaire n'existe pas.");

    const result = generateGrid({
      calendar: {
        year: cal.value.year,
        periods: cal.value.periods,
        closures: cal.value.closures,
      },
      profile: prof.value.parameters,
      plans: plans.value,
      roomTypes: types.value,
      from: input.from,
      to: input.to,
    });

    // The write is the DATABASE's guarded one — this service never upserts a
    // rate itself, so it cannot become a second path that forgets the marker.
    const stored = await this.rates.generateRates(result.rates);
    if (!stored.ok) return stored;

    return ok({
      proposed: result.rates.length,
      written: stored.value.written,
      skipped: stored.value.skipped,
      uncoveredDates: result.uncoveredDates,
      unpricedRoomTypeIds: result.unpricedRoomTypeIds,
      closedDates: result.closedDates,
    });
  }

  // ── The gate, once ───────────────────────────────────────────────────────

  private async read<T>(fn: () => Promise<Result<T>>): Promise<Result<T>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_READ);
    return denied ? denied : fn();
  }

  private async write<T>(fn: () => Promise<Result<T>>): Promise<Result<T>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    return denied ? denied : fn();
  }
}
